import React, { useState, useEffect, useCallback } from 'react';
import styles from './CreateSurvey.module.scss';
import TextField from '../../../components/textField/TextField';
import Dropdown from '../../../components/dropdown/Dropdown';
import Button from '../../../components/button/Button';
import Breadcrumbs from '../../../components/breadcrumbs/Breadcrumbs';
import LoadingOverlay from '../../../components/loadingOverlay/LoadingOverlay';
import { useNavigate, useParams } from 'react-router-dom';
import axiosClient from '../../../api/axiosClient';
import { useToasts } from '../../../contexts/ToastContext';
import SurveyQuestions from './SurveyQuestions';

const CreateSurvey = () => {
  const navigate = useNavigate();
  const { showToast } = useToasts();
  const { surveyId } = useParams();
  const [isLoading, setIsLoading] = useState(false);
  const [isSaving, setIsSaving] = useState(false);
  const [errors, setErrors] = useState({});
  const [formData, setFormData] = useState({ title: '', type: '' });
  const [initialData, setInitialData] = useState(null);
  const [questions, setQuestions] = useState([]);

  const fetchSurvey = useCallback(async () => {
    if (!surveyId) return;
    setIsLoading(true);
    try {
      const response = await axiosClient.get(`/surveys/${surveyId}`);
      const data = response.data?.survey || response.survey || response.data || response;
      const fetched = { title: data.title || '', type: data.type || '' };
      setFormData(fetched);
      setInitialData(fetched);
      setQuestions(data.questions || []);
    } catch (error) {
      showToast(error.message || 'Failed to fetch survey', 'error');
      navigate('/surveys');
    } finally {
      setIsLoading(false);
    }
  }, [surveyId, showToast, navigate]);

  useEffect(() => {
    fetchSurvey();
  }, [fetchSurvey]);

  const validate = () => {
    const newErrors = {};
    if (!formData.title?.trim()) newErrors.title = 'Title is required';
    if (!formData.type?.trim()) newErrors.type = 'Type is required';
    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleChange = (field, value) => {
    setFormData(prev => ({ ...prev, [field]: value }));
    if (errors[field]) setErrors(prev => ({ ...prev, [field]: undefined }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!validate()) return;

    setIsSaving(true);
    try {
      const payload = { title: formData.title, type: formData.type };
      if (surveyId) {
        await axiosClient.patch(`/surveys/${surveyId}`, payload);
        showToast('Survey updated successfully');
        await fetchSurvey();
      } else {
        await axiosClient.post('/surveys', payload);
        showToast('Survey created successfully');
        navigate('/surveys');
      }
    } catch (error) {
      showToast(error.message || 'Failed to save survey', 'error');
    } finally {
      setIsSaving(false);
    }
  };

  const isFormDirty = () => {
    if (!initialData) return true;
    return formData.title !== initialData.title || formData.type !== initialData.type;
  };

  return (
    <>
      {(isLoading || isSaving) && <LoadingOverlay />}
      <div className={styles.breadcrumbs}>
        <Breadcrumbs
          breadcrumbs={[
            { label: 'Surveys', path: '/surveys' },
            { label: surveyId ? 'Edit Survey' : 'Create Survey' }
          ]}
        />
      </div>
      <div className={styles.container}>
        <form className={styles.form} onSubmit={handleSubmit} noValidate>
          <h2>Survey Details</h2>
          <div className={styles.row}>
            <div className={styles.column50}>
              <TextField
                label="Title *"
                value={formData.title}
                onChange={(val) => handleChange('title', val)}
                placeholder="Enter survey title"
                error={errors.title}
              />
            </div>
            <div className={styles.column50}>
              <Dropdown
                label="Type *"
                value={formData.type}
                onChange={(val) => handleChange('type', val)}
                options={[
                  { value: 'ClinicalSite', label: 'Clinical Site' },
                  { value: 'Instructor', label: 'Instructor' },
                ]}
                error={errors.type}
              />
            </div>
          </div>
          {(!surveyId || isFormDirty()) && (
            <div className={styles.actions}>
              <Button
                label="Cancel"
                variant="white"
                size="medium"
                onClick={() => {
                  if (surveyId && initialData) {
                    setFormData(initialData);
                    setErrors({});
                  } else {
                    navigate('/surveys');
                  }
                }}
              />
              <Button
                label={surveyId ? 'Save Survey' : 'Create Survey'}
                variant="primary"
                size="medium"
                type="submit"
              />
            </div>
          )}
        </form>
        {surveyId && (
          <SurveyQuestions
            questions={questions}
            surveyId={surveyId}
            onRefresh={fetchSurvey}
          />
        )}
      </div>
    </>
  );
};

export default CreateSurvey;
